// src/components/TestCode/TestCode.js
import React, { useState, useEffect } from 'react';
import { FaDesktop, FaMobileAlt } from 'react-icons/fa';
import Notification from '../Notification/Notification';
import CodeMirror from '@uiw/react-codemirror';
import { html } from '@codemirror/lang-html';
import { linter, lintGutter } from '@codemirror/lint';
import './TestCode.css';

// Custom HTML linter with error and warning detection
const htmlLinter = linter((view) => {
  const diagnostics = [];
  const content = view.state.doc.toString();

  // Track unclosed tags
  const unclosedTags = [];
  const tags = content.match(/<\/?[^>]+(>|$)/g) || [];

  // Check for syntax errors (unclosed/mismatched tags) and missing attributes
  tags.forEach((tag, index) => {
    const tagStart = content.indexOf(tag, index > 0 ? content.indexOf(tags[index - 1]) + tags[index - 1].length : 0);

    // Check for unclosed tags
    if (tag.startsWith('</')) {
      const tagName = tag.match(/<\/([a-zA-Z0-9]+)/)?.[1];
      if (tagName) {
        const lastOpen = unclosedTags.pop();
        if (lastOpen !== tagName) {
          diagnostics.push({
            from: tagStart,
            to: tagStart + tag.length,
            severity: 'error',
            message: `Mismatched closing tag: expected </${lastOpen}> but got </${tagName}>`,
            render: (view, diagnostic) => {
              const line = view.state.doc.lineAt(diagnostic.from);
              return { from: line.from, to: line.to }; // Highlight the entire line
            },
          });
        }
      }
    } else if (!tag.includes('/>')) {
      const tagName = tag.match(/<([a-zA-Z0-9]+)/)?.[1];
      if (tagName && !['br', 'img', 'input', 'meta', 'hr'].includes(tagName.toLowerCase())) {
        unclosedTags.push(tagName);
      }
    }

    // Check for missing alt and title attributes
    if (tag.startsWith('<') && !tag.startsWith('</')) {
      const tagName = tag.match(/<([a-zA-Z0-9]+)/)?.[1]?.toLowerCase();
      const hasAlt = tag.includes('alt=') || tag.includes('alt =');
      const hasTitle = tag.includes('title=') || tag.includes('title =');

      // Missing alt on <img> tags (warning)
      if (tagName === 'img' && !hasAlt) {
        diagnostics.push({
          from: tagStart,
          to: tagStart + tag.length,
          severity: 'warning',
          message: 'Missing alt attribute on <img> tag',
          render: (view, diagnostic) => {
            const line = view.state.doc.lineAt(diagnostic.from);
            return { from: line.from, to: line.to }; // Highlight the entire line
          },
        });
      }

      // Missing title on <a> tags (warning)
      if (tagName === 'a' && !hasTitle) {
        diagnostics.push({
          from: tagStart,
          to: tagStart + tag.length,
          severity: 'warning',
          message: `Missing title attribute on <${tagName}> tag`,
          render: (view, diagnostic) => {
            const line = view.state.doc.lineAt(diagnostic.from);
            return { from: line.from, to: line.to }; // Highlight the entire line
          },
        });
      }
    }
  });

  // Check for unclosed tags
  unclosedTags.forEach((tag) => {
    const pos = content.lastIndexOf(`<${tag}`);
    diagnostics.push({
      from: pos,
      to: pos + tag.length + 2,
      severity: 'error',
      message: `Unclosed tag: <${tag}>`,
      render: (view, diagnostic) => {
        const line = view.state.doc.lineAt(diagnostic.from);
        return { from: line.from, to: line.to }; // Highlight the entire line
      },
    });
  });

  // Store diagnostics in a global variable for the component to access
  window.diagnostics = diagnostics.map(diagnostic => {
    const line = view.state.doc.lineAt(diagnostic.from);
    return {
      ...diagnostic,
      lineNumber: line.number,
      lineContent: line.text.trim(),
    };
  });

  return diagnostics;
});

const TestCode = () => {
    const [code, setCode] = useState(`<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>Test Email Template - Srihari Muppalla</title>
  <style>
    @media screen and (max-width: 599px) {
      .width100 {
        width: 100% !important;
        display: block !important;
      }
    }
  </style>
</head>
<body style="margin:0; padding:0; font-family: Arial, sans-serif; background-color:#f5f5f5;">
  <table align="center" width="100%" cellpadding="0" cellspacing="0" role="presentation" class="width100" style="background-color: #f5f5f5; padding: 20px 0px 20px 0px;">
    <tr>
      <td>
        <!-- Outer Container -->
        <table align="center" cellpadding="0" cellspacing="0" width="600" role="presentation" class="width100" style="background-color: #ffffff; border-radius: 6px;">
         
          <!-- Banner Section -->
          <tr>
            <td style="background-color: #e0f0ff; padding: 30px 40px; text-align: center; border-top-left-radius: 6px; border-top-right-radius: 6px;">
              <h1 style="color: #004080; font-size: 24px; margin: 0;">&#128231; Test Email Templates</h1>
              <p style="color: #0066cc; font-size: 16px; margin: 10px 0 5px;">Easily Preview &amp; Send Test Emails</p>
              <p style="color: #333333; font-size: 14px; margin: 0;">Developed by <strong>Srihari Muppalla</strong></p>
            </td>
          </tr>
          <!-- Instructions Section -->
          <tr>
            <td style="padding: 25px 40px;">
              <p style="font-size: 15px; color: #444444; line-height: 1.6;">
                Use this tool to preview how your email template looks on both desktop and mobile devices &mdash; and send test emails right to your inbox.
              </p>
              <h3 style="color: #333333; font-size: 18px; margin-top: 25px;">Steps to Use:</h3>
              <table cellpadding="0" cellspacing="0" style="color: #444444; font-size: 15px; line-height: 1.6;">
                <tr>
                  <td style="padding: 5px 0px 5px 20px; vertical-align: top; width: 30px;">1.</td>
                  <td style="padding: 5px 0;"><strong>Paste your email HTML code</strong> into the editor.</td>
                </tr>
                <tr>
                  <td style="padding: 5px 0px 5px 20px; vertical-align: top; width: 30px;">2.</td>
                  <td style="padding: 5px 0;"><strong>Check the live preview</strong> for both desktop and mobile views.</td>
                </tr>
                <tr>
                  <td style="padding: 5px 0px 5px 20px; vertical-align: top; width: 30px;">3.</td>
                  <td style="padding: 5px 0;"><strong>Enter a subject line</strong> for the test email.</td>
                </tr>
                <tr>
                  <td style="padding: 5px 0px 5px 20px; vertical-align: top; width: 30px;">4.</td>
                  <td style="padding: 5px 0;"><strong>Add your email address</strong> where you want to receive the test.</td>
                </tr>
                <tr>
                  <td style="padding: 5px 0px 5px 20px; vertical-align: top; width: 30px;">5.</td>
                  <td style="padding: 5px 0;"><strong>Click &ldquo;Send Test&rdquo;</strong> to receive the email and check how it renders in your inbox.</td>
                </tr>
              </table>
              <p style="margin-top: 25px; background-color: #fff8e1; padding: 15px; border-left: 5px solid #ffc107; font-size: 14px; color: #555;">
                &#128161; <strong>Tip:</strong> Make sure your HTML has inline styles and uses fully qualified image URLs for best results.
              </p>
            </td>
          </tr>
          <!-- Thanks Section -->
          <tr>
            <td style="padding: 0 40px; text-align: center;">
              <p style="color: #666666; font-size: 14px; margin: 0 0 20px;">Thank you for using the Email Template Tester.</p>
            </td>
          </tr>
          <!-- Footer -->
          <tr>
            <td style="background-color: #eaf4ff; text-align: center; padding: 20px; font-size: 12px; color: #336699; border-bottom-left-radius: 6px; border-bottom-right-radius: 6px;">
              &copy; 2025 Srihari Muppalla. All rights reserved.
            </td>
          </tr>
        </table>
      </td>
    </tr>
  </table>
</body>
</html>
`);
  const [isMobileView, setIsMobileView] = useState(false);
  const [customWidth, setCustomWidth] = useState(1200);
  const [subject, setSubject] = useState('');
  const [email, setEmail] = useState('');
  const [notification, setNotification] = useState({ message: '', type: '' });
  const [loading, setLoading] = useState(false);
  const [diagnostics, setDiagnostics] = useState([]);
  const [showIssues, setShowIssues] = useState(false);
  const [activeTab, setActiveTab] = useState('errors'); // 'errors' or 'warnings'

  // Update diagnostics whenever the code changes
  useEffect(() => {
    const interval = setInterval(() => {
      if (window.diagnostics) {
        setDiagnostics(window.diagnostics);
      }
    }, 500); // Check every 500ms for updated diagnostics
    return () => clearInterval(interval);
  }, []);

  const handleCodeChange = (value) => {
    setCode(value);
  };

  const handleWidthChange = (e) => {
    const value = e.target.value;
    if (value === '') {
      setCustomWidth(0);
    } else {
      const numValue = parseInt(value);
      if (!isNaN(numValue)) {
        setCustomWidth(numValue);
      }
    }
  };

  const handleMobileView = () => {
    setIsMobileView(true);
    setCustomWidth(320);
  };

  const handleDesktopView = () => {
    setIsMobileView(false);
    setCustomWidth(1200);
  };

  const handleSendTest = async (e) => {
    e.preventDefault();
    if (!email || !subject) {
      setNotification({ message: 'Please enter both email and subject.', type: 'error' });
      return;
    }

    setLoading(true);
    setNotification({ message: '', type: '' });

    try {
      const response = await fetch('https://sriharim.in/php/send-test.php', { // Updated URL to point to the PHP script
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, subject, html: code }),
      });

      if (!response.ok) {
        throw new Error('Failed to send test email');
      }

      const result = await response.json();
      if (result.status === 'success') {
        setNotification({ message: result.message || 'Test email sent successfully!', type: 'success' });
        setTimeout(() => setNotification({ message: '', type: '' }), 5000);
      } else {
        setNotification({ message: result.message || 'Failed to subscribe.', type: 'error' });
      }
      
    } catch (error) {
      setNotification({ message: `Error: ${error.message}`, type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const closeNotification = () => {
    setNotification({ message: '', type: '' });
  };

  // Determine preview styles based on customWidth
  const previewStyles = customWidth < 650 
    ? { width: `${customWidth}px`, maxWidth: 'none' }
    : { width: '100%', maxWidth: '1200px' };

  // Create a full HTML document for the iframe
  const iframeContent = `${code}`;

  // Filter errors and warnings
  const errors = diagnostics.filter(d => d.severity === 'error');
  const warnings = diagnostics.filter(d => d.severity === 'warning');

  return (
    <div className="testcode-container">
      <div className="testcode-header">
        <input
          type="text"
          className="testcode-subject-input"
          value={subject}
          onChange={(e) => setSubject(e.target.value)}
          placeholder="Subject Line"
        />
        <input
          type="email"
          className="testcode-email-input"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="Email Address"
        />
        <button
          className="testcode-send-button"
          onClick={handleSendTest}
          disabled={loading}
        >
          {loading ? 'Sending...' : 'Send Test'}
        </button>
      </div>
      <Notification
        message={notification.message}
        type={notification.type}
        onClose={closeNotification}
      />
      <div className="testcode-main">
        <div className="testcode-left">
          <div className="testcode-editor-wrapper">
            <CodeMirror
              value={code}
              height="100%"
              extensions={[html(), htmlLinter, lintGutter()]}
              onChange={handleCodeChange}
              theme="light"
              className="testcode-input"
              basicSetup={{
                lineNumbers: true,
                highlightActiveLine: true,
                bracketMatching: true,
                closeBrackets: true,
                autocompletion: true,
              }}
            />
            {(errors.length > 0 || warnings.length > 0) && (
              <>
                {showIssues && (
                  <div className="testcode-issues-section">
                    <div className="testcode-issues-tabs">
                      <button
                        className={`testcode-tab ${activeTab === 'errors' ? 'active' : ''}`}
                        onClick={() => setActiveTab('errors')}
                      >
                        Errors ({errors.length})
                      </button>
                      <button
                        className={`testcode-tab ${activeTab === 'warnings' ? 'active' : ''}`}
                        onClick={() => setActiveTab('warnings')}
                      >
                        Warnings ({warnings.length})
                      </button>
                    </div>
                    <div className="testcode-issues-content">
                      {activeTab === 'errors' && (
                        <div className="testcode-issues-list">
                          {errors.length > 0 ? (
                            errors.map((error, index) => (
                              <div key={index} className="testcode-issue-item testcode-error">
                                <span>Line {error.lineNumber}: </span>
                                <span>{error.message}</span>
                                <pre>{error.lineContent}</pre>
                              </div>
                            ))
                          ) : (
                            <p>No errors found.</p>
                          )}
                        </div>
                      )}
                      {activeTab === 'warnings' && (
                        <div className="testcode-issues-list">
                          {warnings.length > 0 ? (
                            warnings.map((warning, index) => (
                              <div key={index} className="testcode-issue-item testcode-warning">
                                <span>Line {warning.lineNumber}: </span>
                                <span>{warning.message}</span>
                                <pre>{warning.lineContent}</pre>
                              </div>
                            ))
                          ) : (
                            <p>No warnings found.</p>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                )}
                <div
                  className="testcode-issues-bar"
                  onClick={() => setShowIssues(!showIssues)}
                >
                  {errors.length > 0 || warnings.length > 0
                    ? `Issues: ${errors.length} Errors, ${warnings.length} Warnings`
                    : 'No Issues'}
                </div>
              </>
            )}
          </div>
        </div>
        <div className="testcode-right">
          <div className="testcode-preview-controls">
            <input
              type="number"
              className="testcode-width-input"
              value={customWidth}
              onChange={handleWidthChange}
            />
            <FaDesktop
              className={`testcode-icon testcode-desktop-icon ${!isMobileView ? 'testcode-active' : ''}`}
              onClick={handleDesktopView}
            />
            <FaMobileAlt
              className={`testcode-icon testcode-mobile-icon ${isMobileView ? 'testcode-active' : ''}`}
              onClick={handleMobileView}
            />
          </div>
          <div className="testcode-preview-wrapper">
            <iframe
              className="testcode-preview"
              style={previewStyles}
              srcDoc={iframeContent}
              title="Preview"
              sandbox="allow-same-origin"
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default TestCode;